import { ComponentId } from "@omegagrid/core";
import type { Engine } from "./engine";
import type { CustomParams, QueryResults, MultiQueryResults, QueryResult, AbortablePromise } from "../types";
import { CsvArrayStream } from "@reporting/rp-utils-csv";
import { SqlDataset, SqlExecCallbackArgs, readResultStream } from "@reporting/rp-app-host/src/client/sqlStream";
import * as helpers from "@reporting/rp-app-host/src/helpers";
import type { SqlError } from "@reporting/rp-app-host/src/types";

const TYPE_STRING = 1;
const TYPE_NUMBER = 2;
const TYPE_BOOLEAN = 3;

export type ColumnType = typeof TYPE_STRING | typeof TYPE_NUMBER | typeof TYPE_BOOLEAN;
export type ColumnDefinition = [string, ColumnType];
export type RowProcessor = (row: string[], columns: ColumnDefinition[]) => Record<string, unknown>;
export type Value = string|number|boolean|null;

export const convertValue = (value: string, type: ColumnType): Value => {
	if (value === null || value === undefined) return null;
	switch (type) {
		case TYPE_BOOLEAN:
			return value === '1' || value.toLowerCase() === 'true';
		case TYPE_NUMBER:
			return parseFloat(value);
		default:
			return value;
	}
}

const getColumnType = (type: string) : ColumnType => {
	if (/bit/i.test(type)) return TYPE_BOOLEAN;
	if (/int/i.test(type)) return TYPE_NUMBER;
	if (/float/i.test(type)) return TYPE_NUMBER;
	if (/double/i.test(type)) return TYPE_NUMBER;
	if (/decimal/i.test(type)) return TYPE_NUMBER;
	if (/numeric/i.test(type)) return TYPE_NUMBER;
	if (/money/i.test(type)) return TYPE_NUMBER;
	return TYPE_STRING;
}

const convertToQueryResults = (data: SqlDataset, processRow?: RowProcessor): QueryResult => {
	const columns =  data.columns.map<ColumnDefinition>(col => [col.name, getColumnType(col.type)]);
	let value: number|string|boolean|null;

	processRow ??= (sourceRow, columns) => {
		const row: Record<string, typeof value> = {};
		columns.forEach((col, i) => {
			value = convertValue(sourceRow[i], col[1]);
			if (value !== null) row[col[0]] = value;
		});
		return row;
	}

	return data.data.map(sourceRow => processRow(sourceRow, columns));
}

export class DataRelay {

	private _resolutions = new Map<string, [(result: any) => void, (error: any) => void][]>();
	private _results = new Map<string, any>();

	/** called with every freshly loaded result - the embedding host mirrors the cache for iframe reloads */
	onResult?: (hash: string, value: unknown) => void;

	/** preload results cached by the embedding host before a previous reload of this page */
	seed(entries: [string, unknown][]) {
		for (const [hash, value] of entries) {
			this._results.set(hash, value);
		}
	}

	clear() {
		this._results.clear();
		this._resolutions.clear();
	}

	get ms() { return this.engine.ms }

	constructor(readonly engine: Engine) {}

	async execResource(idResource: ComponentId, params?: CustomParams): Promise<MultiQueryResults> {
		const hash = await helpers.hashItems(idResource, params);
		if (this._results.has(hash)) return this._results.get(hash);
		return new Promise((resolve, reject) => {
			if (this._resolutions.has(hash)) {
				this._resolutions.get(hash).push([resolve, reject]);
				return;
			}

			this._resolutions.set(hash, [[resolve, reject]]);

			this.ms.get<Record<string, QueryResults>>(`execResource/${idResource}`, {
				customParams: params,
				id_context: this.engine.context?.id_context,
			}, {throwException: true}).then(results => {
				const map = new Map<string, QueryResults>();
				for (const [key, value] of Object.entries(results)) {
					map.set(key, value);
				}
				this._results.set(hash, map);
				this.onResult?.(hash, map);
				const resolutions = this._resolutions.get(hash);
				if (resolutions) {
					for (const [resolve] of resolutions) {
						resolve(map);
					}
					this._resolutions.delete(hash);
				}
			}).catch(error => {
				const resolutions = this._resolutions.get(hash);
				if (resolutions) {
					for (const [, reject] of resolutions) {
						reject(error);
					}
					this._resolutions.delete(hash);
				}
			});
		});
	}

	async exec(idQuery: ComponentId, idResource: ComponentId, params?: CustomParams): Promise<QueryResults> {
		const hash = await helpers.hashItems(idQuery, idResource, params);
		let promise: AbortablePromise<QueryResults>
		if (this._results.has(hash)) {
			promise = new Promise<QueryResults>(resolve => resolve(this._results.get(hash)));
			promise.abort = () => {};
			return promise;
		}

		const abortController = new AbortController();

		promise = new Promise((resolve, reject) => {
			if (this._resolutions.has(hash)) {
				this._resolutions.get(hash).push([resolve, reject]);
				return;
			}

			this._resolutions.set(hash, [[resolve, reject]]);

			this.ms.get<QueryResults>(`exec/${idQuery}`, {
				customParams: params,
				id_resource: idResource,
				id_context: this.engine.context?.id_context
			}, {
				signal: abortController.signal,
				headers: {accept: 'application/json'}
			}).then(result => {
				this._results.set(hash, result);
				this.onResult?.(hash, result);
				const resolutions = this._resolutions.get(hash);
				if (resolutions) {
					for (const [resolve] of resolutions) {
						resolve(result);
					}
					this._resolutions.delete(hash);
				}
			}).catch(error => {
				const resolutions = this._resolutions.get(hash);
				if (resolutions) {
					for (const [, reject] of resolutions) {
						reject(error);
					}
					this._resolutions.delete(hash);
				}
			});
		});

		promise.abort = () => {
			abortController.abort();
		};

		return promise;
	}

	async execStream(idQuery: ComponentId, idResource: ComponentId, params?: CustomParams, processRow?: RowProcessor): Promise<QueryResults> {
		let response: Response;
		try {
			response = await this.ms.getRaw(`exec/${idQuery}`, {
				csvParams: {
					quote: '"',
					delimiter: '\t',
					header: true,
					headerJson: true,
					nullValue: 'NULL'
				},
				customParams: params,
				// the parameters of the query are filled in from the report context (custom drill)
				id_context: this.engine.context?.id_context
			}, {headers: {accept: 'text/csv'}});
		} catch (error) {
			throw new Error(`Failed to execute query ${idQuery} for resource ${idResource}: ${error}`);
		}

		const reader = response.body.pipeThrough(new CsvArrayStream({quote: '"', delimiter: '\t', nullValue: 'NULL'})).getReader();
		const results: QueryResults = [];
		const errors: SqlError[] = [];
		await readResultStream(reader, (args: SqlExecCallbackArgs) => {
			if (args?.message?.errors?.length) errors.push(...args.message.errors);
			if (!args.dataset) return;
			results.push(convertToQueryResults(args.dataset, processRow));
		});
		if (errors.length) {
			throw new Error(`${errors.map(error => error.error).join("\n")}`);
		}
		return results;
	}

}
